/**
 * PER-PRODUCT ACCENT — the colour a product's own packaging wears.
 *
 * The public site has coloured every product card by its packaging artwork since the site redesign;
 * inside the app the same products were drawn in one flat brand green, so a "stock value by product"
 * chart was ten identical bars distinguishable only by reading the axis labels. A product that has a
 * colour on the shelf and on the website should keep it wherever the app draws it.
 *
 * This module is the SINGLE source of truth for that map. `site/lib/accent.ts` re-exports it rather
 * than keeping a second copy, so adding a product's tint in one place colours the marketing card, the
 * app's charts and anything added later.
 *
 * The values are real hex on purpose. ECharts paints to a canvas, which has no CSS cascade — a
 * `var(--gb-*)` there renders BLACK (see lib/palette.ts, which exists for the same reason).
 *
 * A COSMETIC tint only — never backend data, and never load-bearing: an unmapped product falls back
 * to its category accent, then to brand green, and nothing depends on which it got.
 */

const PRODUCT_ACCENT: Record<string, string> = {
  agn: '#1E93AE',
  'agn-lte': '#2C6D4E',
  aminopf: '#9B33B7',
  biodynamic: '#9A6B34',
  biovin: '#137E3E',
  'c4l-biofoliar': '#D0951F',
  'fulvic-25': '#8A2D54',
  'natures-green-4-plus': '#3FA82A',
  'nature-s-green-4-plus': '#3FA82A',
  'plant-armor-elite': '#D5A635',
  hydretain: '#1F86C9',
};

const BRAND_GREEN = '#159A3C';

/** Fallback ramp for products with no packaging tint of their own — brand-adjacent, and stable per
 *  name so the same product keeps the same bar colour between renders and between pages. */
const FALLBACK_RAMP = ['#339022', '#e7b22c', '#1677ff', '#80ce63', '#a87a16', '#6b4226', '#45a82b', '#4596ff'];

const COMBINING_MARKS = /[̀-ͯ]/g;

/** Product name → the slug the accent map is keyed by. Mirrors `site/lib/slug.ts`. */
export function productSlug(input: string): string {
  return String(input ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .replace(/\+/g, ' plus ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

/** Accent by SLUG — the form the site already holds. */
export function productAccent(slug: string, categoryAccent?: string | null): string {
  return PRODUCT_ACCENT[slug] ?? categoryAccent ?? BRAND_GREEN;
}

/**
 * Accent by the product NAME a list or chart actually has in hand.
 *
 * Names in the app carry their pack size ("Bioshakti 25 Kg", "Biovin Powder", "AGN 500ml"), which no
 * slug in the map matches, so the lookup walks the name back one word at a time: `biovin-powder` →
 * `biovin`. That is what makes a variant inherit its product's colour instead of falling through to
 * a generic green, and it is why this is not a plain map lookup.
 *
 * Unmapped names get a STABLE colour from the fallback ramp rather than all sharing one green —
 * otherwise a chart of ten unmapped products is exactly the flat picture this replaces.
 */
export function accentForProductName(name?: string | null): string {
  const slug = productSlug(name ?? '');
  if (!slug) return BRAND_GREEN;
  if (PRODUCT_ACCENT[slug]) return PRODUCT_ACCENT[slug];

  const parts = slug.split('-');
  for (let take = parts.length - 1; take >= 1; take--) {
    const hit = PRODUCT_ACCENT[parts.slice(0, take).join('-')];
    if (hit) return hit;
  }

  // FNV-1a over the slug: same product, same colour, every render and every page.
  let h = 0x811c9dc5;
  for (let i = 0; i < slug.length; i++) {
    h ^= slug.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return FALLBACK_RAMP[Math.abs(h) % FALLBACK_RAMP.length];
}
